import React, { useMemo } from 'react';
import { useTranslation } from 'react-i18next';
import { LiliButton } from '@lili/lili-frontend-common';
import { LiliPlanType } from '../../../flows/planUpgrade/constants';
import { ReactComponent as PlanIcon } from '../../../assets/icons/svg/shared/ic-shared-plan.svg';
import { featureAccess, LimitedFeatureType } from '../../../services/appAccess.service';
import { FEATURE_FLAG } from '../../../constants/general';
import { FeatureAccess } from './rolesAndPermissions/PermissionsList';

export interface SettingsItemProps {
  onUpgradeClick: () => void;
  plan: LiliPlanType;
  planFee: number;
  isAnnual: boolean;
}

export const AccountSettingsPlanItem = ({ onUpgradeClick, plan, planFee, isAnnual }: SettingsItemProps) => {
  const { t } = useTranslation();
  const planUpgradePermission = featureAccess(FEATURE_FLAG.PLAN_UPGRADE, LimitedFeatureType.UpgradePlan);
  const planPeriodText = isAnnual ? t('ACCOUNT_SETTINGS.ANNUALLY') : t('ACCOUNT_SETTINGS.MONTH');
  const planProps = useMemo(() => {
    switch (plan) {
      case LiliPlanType.BASIC:
      case LiliPlanType.BASIC_V2:
        return {
          name: t('ACCOUNT_SETTINGS.PLAN_PROMOTION_BASIC.NAME'),
          title: t('ACCOUNT_SETTINGS.PLAN_PROMOTION_BASIC.TITLE'),
          description: t('ACCOUNT_SETTINGS.PLAN_PROMOTION_BASIC.DESCRIPTION'),
        };

      case LiliPlanType.PRO:
      case LiliPlanType.PRO_V2:
        return {
          name: t('ACCOUNT_SETTINGS.PLAN_PROMOTION_PRO.NAME'),
          title: t('ACCOUNT_SETTINGS.PLAN_PROMOTION_PRO.TITLE'),
          description: t('ACCOUNT_SETTINGS.PLAN_PROMOTION_PRO.DESCRIPTION'),
        };
      case LiliPlanType.SMART:
        return {
          name: t('ACCOUNT_SETTINGS.PLAN_PROMOTION_SMART.NAME'),
          title: t('ACCOUNT_SETTINGS.PLAN_PROMOTION_SMART.TITLE'),
          description: t('ACCOUNT_SETTINGS.PLAN_PROMOTION_SMART.DESCRIPTION'),
        };
      case LiliPlanType.PREMIUM:
        return {
          name: t('ACCOUNT_SETTINGS.PLAN_PROMOTION_PREMIUM.NAME'),
          title: null,
          description: null,
        };
      default:
        return {
          name: '',
          title: null,
          description: null,
        };
    }
  }, [plan]);

  return (
    <div className='flex flex-row border-bw03 border-[1px]  rounded-xl shadow-widgetCard pl-[32px] pr-[10px] py-[12px] bg-bw00 w-full'>
      <div className='flex flex-col basis-[36%]'>
        <div className='flex flex-col py-[12px]'>
          <p className='text-bw08 text-[24px] font-metricRMedium'>{t('ACCOUNT_SETTINGS.PLAN_DETAILS_SECTION')}</p>
          <div className='flex flex-row items-center gap-[8px] py-[10px] justify-between'>
            <PlanIcon className='text-bw08 w-[24px] h-[24px] mr-[8px]' />
            <div className='flex flex-col w-full'>
              <p className='text-bw08 text-[16px] font-metricRMedium'>{t('ACCOUNT_SETTINGS.CURRENT_PLAN')}</p>
              <p
                id='planValue'
                className='text-bw07 text-[16px] font-metricR'
              >
                {`${planProps.name} $${planFee} / ${planPeriodText}`}
              </p>
            </div>
          </div>
        </div>
      </div>

      <div
        className={`flex flex-row bg-action00 basis-[64%] gap-[16px] items-center px-[32px] justify-between  rounded-md ${plan === LiliPlanType.PREMIUM && 'opacity-0 min-w-[600px]'}`}
      >
        <div className='flex flex-col gap-2'>
          <p className='text-bw08 text-[22px] font-metricRSemibold  leading-[128%]'>{planProps.title}</p>
          <p className='text-bw07 text-[14px] font-metricRSemibold'>{planProps.description}</p>
        </div>
        <div className='min-w-[168px]'>
          {planUpgradePermission === FeatureAccess.READ_WRITE && (
            <LiliButton
              id='upgrade-plan-button'
              onClick={onUpgradeClick}
              title={t('ACCOUNT_SETTINGS.UPGRADE_BUTTON')}
              className='w-[168px]'
            />
          )}
        </div>
      </div>
    </div>
  );
};
